import { createApiClient } from "../../../api/apiClient";
import { AppDispatch } from "../../../store";
import {
  setZones,
  setLoading,
  setError,
} from "../../../store/slices/leaderShipWalkSlice";
import {
  handleLeadershipWalkErrorWithFriendlyMessage,
  LEADERSHIP_WALK_API_NAMES,
} from "../../../store/utils/leadershipWalkErrorHandler";

interface FetchZonesParams {
  location: string;
  frequency?: string;
  dispatch: AppDispatch;
}

export const fetchZones = async ({
  location,
  frequency,
  dispatch,
}: FetchZonesParams): Promise<void> => {
  dispatch(setLoading(true));
  dispatch(setError(null));

  try {
    const api = await createApiClient();
    let endpoint = `/leadership-walks/zones?location=${encodeURIComponent(
      location
    )}`;

    if (frequency) {
      endpoint += `&frequency=${encodeURIComponent(frequency)}`;
    }

    const response = await api.get(endpoint);

    // Validate response
    if (!response.data || !Array.isArray(response.data)) {
      // Provide a clear user-facing error message for empty/invalid data
      const userFacingError = "Fetching zones failed. Invalid response from server. Please try again.";

      handleLeadershipWalkErrorWithFriendlyMessage(
        LEADERSHIP_WALK_API_NAMES.FETCH_ZONES,
        userFacingError,
        true,
        { location, frequency, reason: "Invalid response structure" }
      );

      dispatch(setError(userFacingError));
      return;
    }

    // Transform the API response to match the Zone interface
    const transformedZones = response.data.map((zone) => ({
      id: String(zone.ZONE_ID),
      name: zone.ZONE,
      displayText: zone.ZONE,
      ZONE_ID: zone.ZONE_ID,
      ZONE: zone.ZONE,
    }));

    // Update Redux store with zones
    dispatch(setZones(transformedZones));
  } catch (error: any) {
    console.error("Error fetching zones:", error);
    // Build a clear user-facing message and include retry suggestion
    const originalMessage = error?.message || String(error);
    const isNetwork = /network/i.test(originalMessage) || error?.code === 'ENOTFOUND' || error?.code === 'ERR_NETWORK';
    const userFacingError = isNetwork
      ? `Fetching zones failed. ${originalMessage}. Please try again.`
      : `Fetching zones failed. ${originalMessage || 'Please try again.'}`;

    // Use centralized error handler with modal overlay and pass the explicit user message
    handleLeadershipWalkErrorWithFriendlyMessage(
      LEADERSHIP_WALK_API_NAMES.FETCH_ZONES,
      userFacingError,
      true,
      { location, frequency }
    );

    dispatch(setError(userFacingError));
  } finally {
    dispatch(setLoading(false));
  }
};
